import { Button, Flex, Stack, Text, TextInput } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { BulkInviteAuthorisedSignatoriesPreviewRoleRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	role: BulkInviteAuthorisedSignatoriesPreviewRoleRes;
	totalDocuments: number;
	value: { email: string; firstName: string; lastName: string };
	emailValid: boolean;
	onChange: (patch: Partial<{ email: string; firstName: string; lastName: string }>) => void;
	cancelledEmails: string[];
	onToggleCancel: (email: string) => void;
}

export const BulkInviteAuthorisedSignatoryRoleSection = ({
	role,
	totalDocuments,
	value,
	emailValid,
	onChange,
	cancelledEmails,
	onToggleCancel,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<Stack
			gap="xs"
			p="sm"
			sx={(theme) => ({
				border: `1px solid ${theme.colors.gray[2]}`,
				borderRadius: theme.radius.sm,
			})}
		>
			<Flex justify="space-between" align="center">
				<Text fw={600} size="sm">
					{role.roleName}
				</Text>
				<Text size="sm">
					{tMipManage(
						'documents_page.bulk_invite_authorised_signatory_modal.applies_to',
						{
							count: role.openCount,
							total: totalDocuments,
						}
					)}
				</Text>
			</Flex>
			{role.currentInvitees.map((invitee) => {
				const canCancel = invitee.documentCount > invitee.signedDocumentCount;
				const isMarkedForCancel = cancelledEmails.includes(invitee.email);

				return (
					<Flex
						key={invitee.email}
						justify="space-between"
						align="center"
						p="xs"
						sx={(theme) => ({
							backgroundColor: isMarkedForCancel
								? theme.colors.red[0]
								: theme.colors.gray[0],
							borderRadius: theme.radius.sm,
						})}
					>
						<Flex direction="column">
							<Text size="sm">
								{invitee.firstName} {invitee.lastName}
							</Text>
							<Text size="xs">
								{tMipManage(
									'documents_page.bulk_assign_counterparty_modal.invited_on_docs',
									{ count: invitee.documentCount }
								)}
							</Text>
						</Flex>
						{canCancel && (
							<Button
								data-testid={
									TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_CANCEL_TOGGLE
								}
								variant={isMarkedForCancel ? 'filled' : 'subtle'}
								color={isMarkedForCancel ? 'red' : undefined}
								size="xs"
								onClick={() => onToggleCancel(invitee.email)}
							>
								{isMarkedForCancel
									? tMipManage(
											'documents_page.bulk_assign_counterparty_modal.undo_cancel_invite'
										)
									: tMipManage(
											'documents_page.bulk_assign_counterparty_modal.cancel_invite'
										)}
							</Button>
						)}
					</Flex>
				);
			})}
			{role.openCount > 0 && (
				<>
					<TextInput
						data-testid={TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_EMAIL_INPUT}
						label={tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.email'
						)}
						type="email"
						value={value.email}
						error={
							value.email.trim().length > 0 && !emailValid
								? tMipManage(
										'documents_page.bulk_invite_authorised_signatory_modal.invalid_email'
									)
								: undefined
						}
						onChange={(e) => onChange({ email: e.currentTarget.value })}
					/>
					<TextInput
						data-testid={
							TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_FIRST_NAME_INPUT
						}
						label={tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.first_name'
						)}
						value={value.firstName}
						onChange={(e) => onChange({ firstName: e.currentTarget.value })}
					/>
					<TextInput
						data-testid={
							TEST_ELEMENT_IDS.BULK_INVITE_AUTHORISED_SIGNATORY_LAST_NAME_INPUT
						}
						label={tMipManage(
							'documents_page.bulk_invite_authorised_signatory_modal.last_name'
						)}
						value={value.lastName}
						onChange={(e) => onChange({ lastName: e.currentTarget.value })}
					/>
				</>
			)}
		</Stack>
	);
};
